import {
  CalendarOutlined,
  EyeOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Col, Row, Flex } from 'antd';
import { KeyNotesDrawer } from 'components/organisms/oneplaform/key-notes-drawer';
import { useCallback, useMemo, useState } from 'react';
import useUser from 'utils/useUser';
import PatientReadingsSection from 'components/organisms/oneplatform/patient-readings-section';
import DashboardContent from 'components/organisms/oneplatform/dashboard-content';
import DashboardSidebar from 'components/organisms/oneplatform/dashboard-sidebar';
import DashboardLayout from 'components/templates/dashboard-layout';
import ProductivityGoals from 'components/molecules/oneplaform/productivity-goals';
import TodayHistoryActivities from 'components/molecules/oneplaform/today-history-activities';
import TodayAppointments from 'components/molecules/oneplaform/today-appointments';
import TrendingChart from 'components/molecules/oneplaform/trending-chart';
import { useTranslation } from 'react-i18next';
import OverviewPatients from 'components/molecules/oneplaform/overview-patients';
import NextAppointment from 'components/molecules/oneplaform/next-appointment';
import {
  type AppointmentMethod,
  ReadingStatus,
  PresetKeyEnum,
} from 'infrastructure/enums';
import UpdateDueDateModal from 'components/organisms/oneplatform/update-due-date-modal';
import { type Nullable } from 'infrastructure/types';

import {
  TODAY_APPOINTMENTS_PAGE_SIZE,
  PATIENT_READINGS_PAGE_SIZE,
  TODAY_HISTORY_ACTIVITIES_PAGE_SIZE,
} from './constants';
import { useNextAppointment } from './hooks/use-next-appointment';
import { useOverviewPatientSummary } from './hooks/use-overview-patient-summary';
import { usePatientReadings } from './hooks/use-patient-readings';
import { useTimeframePicker } from './hooks/use-timeframe-picker';
import { useTodayAppointments } from './hooks/use-today-appointments';
import { useHistoricalActivities } from './hooks/use-historical-activities';
import { useTrendingChart } from './hooks/use-trending-chart';
import styles from './styles.module.scss';
import { createProductivityMetricsMock } from './mocks';

import type {
  INextAppointmentAction,
  INextAppointmentDetails,
} from 'components/molecules/oneplaform/next-appointment/types';
import type { TProductivityGoalMetric } from 'components/molecules/oneplaform/productivity-goals/types';
import type {
  TTodayAppointmentActionButton,
  TTodayAppointmentItem,
} from 'components/molecules/oneplaform/today-appointments/types';
import type { TPatientAlertFilter } from 'components/organisms/oneplatform/patient-readings-section/types';
import type { IPatientInfo } from 'components/organisms/oneplaform/key-notes-drawer/types';
import type { INextAppointment } from 'infrastructure/interfaces';

const HomeDashboardPage = () => {
  const user = useUser();
  const { t } = useTranslation();

  const [isUpdateDueDateModalOpen, setIsUpdateDueDateModalOpen] =
    useState(false);
  const [
    selectedAppointmentToChangeDueDate,
    setSelectedAppointmentToChangeDueDate,
  ] = useState<INextAppointment | null>(null);

  const [patientReadingsPage, setPatientReadingsPage] = useState<number>(1);
  const [patientReadingsStatus, setPatientReadingsStatus] = useState<
    ReadingStatus | undefined
  >(undefined);
  const [patientReadingsSearchTerm, setPatientReadingsSearchTerm] = useState<
    string | undefined
  >(undefined);

  const handleTimeframeSelectionChange = useCallback(() => {
    setPatientReadingsPage(1);
  }, []);

  const { timeframeFilterProps, dateRangeObject } = useTimeframePicker(
    handleTimeframeSelectionChange,
    PresetKeyEnum.CurrentMonth,
  );

  const { overviewPatientsProps } = useOverviewPatientSummary(user.roleNames, {
    startTime: dayjs().startOf('day').valueOf(),
    endTime: dayjs().endOf('day').valueOf(),
  });

  const patientReadingsTimeRange = useMemo(
    () => ({
      startTime: dayjs().startOf('day').valueOf(),
      endTime: dayjs().endOf('day').valueOf(),
    }),
    [],
  );

  const [todayAppointmentsPage, setTodayAppointmentsPage] = useState<number>(1);
  const [todayHistoryPage, setTodayHistoryPage] = useState<number>(1);
  const patientReadingsParams = useMemo(
    () => ({
      page: patientReadingsPage,
      items: PATIENT_READINGS_PAGE_SIZE,
      startTime: patientReadingsTimeRange.startTime,
      endTime: patientReadingsTimeRange.endTime,
      status: patientReadingsStatus,
      searchTerm: patientReadingsSearchTerm,
      skipMACheck: user.isMedicalAssistantRole || undefined,
    }),
    [
      patientReadingsPage,
      patientReadingsStatus,
      patientReadingsSearchTerm,
      patientReadingsTimeRange.startTime,
      patientReadingsTimeRange.endTime,
    ],
  );

  const {
    details: nextAppointmentDetails,
    isLoading: nextAppointmentLoading,
    isFetching: nextAppointmentFetching,
    scheduleRefresh: scheduleNextAppointmentRefresh,
  } = useNextAppointment({ t });

  const {
    appointments: todayAppointments,
    rawAppointments: todayAppointmentsRaw,
    total: todayAppointmentsTotal,
    isLoading: todayAppointmentsLoading,
    isFetching: todayAppointmentsFetching,
    refetch: refetchTodayAppointments,
  } = useTodayAppointments({
    page: todayAppointmentsPage,
    items: TODAY_APPOINTMENTS_PAGE_SIZE,
    dateFrom: patientReadingsTimeRange.startTime,
    dateTo: patientReadingsTimeRange.endTime,
    nextAppointmentId: nextAppointmentDetails?.id,
    t,
  });

  const {
    entries: todayHistoryEntries,
    total: todayHistoryTotal,
    isLoading: todayHistoryLoading,
    isFetching: todayHistoryFetching,
  } = useHistoricalActivities({
    page: todayHistoryPage,
    items: TODAY_HISTORY_ACTIVITIES_PAGE_SIZE,
    profileGuid: user.loginGuid,
  });

  const {
    patientReadings,
    count,
    isLoading: patientReadingsLoading,
    isFetching: patientReadingsFetching,
  } = usePatientReadings(patientReadingsParams);

  const [patientAlertFilter, setPatientAlertFilter] =
    useState<TPatientAlertFilter>('all');

  const [selectedPatientGuid, setSelectedPatientGuid] = useState<string | null>(
    null,
  );
  const [selectedPatientData, setSelectedPatientData] =
    useState<IPatientInfo | null>(null);
  const [isKeyNoteDrawerOpen, setIsKeyNoteDrawerOpen] = useState(false);

  const handleStatusCardSelect = useCallback(
    (statusId?: string) => {
      if (
        statusId !== 'critical' &&
        statusId !== 'at-risk' &&
        statusId !== 'normal'
      )
        return;

      let statusForFilter: ReadingStatus;
      let localFilterValue: TPatientAlertFilter;

      if (statusId === 'critical') {
        statusForFilter = ReadingStatus.Critical;
        localFilterValue = 'critical';
      } else if (statusId === 'normal') {
        statusForFilter = ReadingStatus.Normal;
        localFilterValue = 'normal';
      } else {
        statusForFilter = ReadingStatus.Risk;
        localFilterValue = 'risk';
      }

      const newStatus =
        patientReadingsStatus === statusForFilter ? undefined : statusForFilter;

      setPatientAlertFilter(newStatus ? localFilterValue : 'all');

      setPatientReadingsStatus(newStatus);
      setPatientReadingsPage(1);
    },
    [patientReadingsStatus],
  );

  const selectedStatusId = useMemo(() => {
    if (patientAlertFilter === 'critical') return 'critical';
    if (patientAlertFilter === 'risk') return 'at-risk';
    if (patientAlertFilter === 'normal') return 'normal';
    return null;
  }, [patientAlertFilter]);

  const productivityMetrics = useMemo<TProductivityGoalMetric[]>(
    () => createProductivityMetricsMock(t),
    [t],
  );

  const {
    categories: trendingCategories,
    series: trendingSeries,
    isLoading: trendingLoading,
    isFetching: trendingFetching,
  } = useTrendingChart({
    t,
    dateRange: dateRangeObject,
  });

  const handleTodayAppointmentNotesClick = useCallback(
    (appointment: TTodayAppointmentItem) => {
      const rawAppointment = todayAppointmentsRaw?.find(
        (raw) => raw.guid === appointment.id,
      );

      if (rawAppointment?.patient) {
        const { patient } = rawAppointment;
        const doctorName = rawAppointment.scheduledWith?.name || '';

        setSelectedPatientData({
          guid: patient.guid,
          firstName: patient.firstName || '',
          lastName: patient.lastName || '',
          birthDate: patient.birthDate || '',
          language: patient.language || '',
          doctorName,
          status: undefined,
          ccmStatus: undefined,
        });
        setSelectedPatientGuid(patient.guid);
        setIsKeyNoteDrawerOpen(true);
      }
    },
    [todayAppointmentsRaw],
  );

  const handleTodayAppointmentChangeDueDateClick = useCallback(
    (appointment: TTodayAppointmentItem) => {
      const rawAppointment = todayAppointmentsRaw?.find(
        (raw) => raw.guid === appointment.id,
      );

      if (rawAppointment) {
        const isDoctor = rawAppointment.scheduledWith?.type === 'doctor';

        const appointmentData: INextAppointment = {
          guid: rawAppointment.guid,
          patientGuid: rawAppointment.patientGuid,
          agencyDoctorGuid: isDoctor
            ? undefined
            : rawAppointment.scheduledWith?.guid,
          doctorGuid: isDoctor ? rawAppointment.scheduledWith?.guid : undefined,
          appointmentMethod:
            (rawAppointment.appointmentMethod as Nullable<AppointmentMethod>) ||
            null,
          scheduledDate: rawAppointment.scheduledDate || '',
          updatedAt: '',
        };
        setSelectedAppointmentToChangeDueDate(appointmentData);
        setIsUpdateDueDateModalOpen(true);
      }
    },
    [todayAppointmentsRaw],
  );

  const todayAppointmentActions = useMemo<TTodayAppointmentActionButton[]>(
    () => [
      {
        id: 'calendar',
        ariaLabel: t('homeDashboard.appointments.actions.openCalendar'),
        tooltip: 'Appointment Due Date',
        icon: <CalendarOutlined />,
        onClick: handleTodayAppointmentChangeDueDateClick,
      },
      {
        id: 'notes',
        ariaLabel: t('homeDashboard.appointments.actions.openNotes'),
        tooltip: 'Key Note',
        icon: <SnippetsOutlined />,
        onClick: handleTodayAppointmentNotesClick,
      },
    ],
    [
      t,
      handleTodayAppointmentNotesClick,
      handleTodayAppointmentChangeDueDateClick,
    ],
  );

  const handleNextAppointmentNotesClick = useCallback(
    (details: INextAppointmentDetails) => {
      if (details?.patient) {
        setSelectedPatientData({
          guid: details.patient.guid,
          firstName: details.patient.name.split(' ')[0] || '',
          lastName: details.patient.name.split(' ').slice(1).join(' ') || '',
          birthDate: details.patient.birthDateLabel || '',
          language: details.patient.languageLabel || '',
          doctorName: '',
          status: undefined,
          ccmStatus: undefined,
        });
        setSelectedPatientGuid(details.patient.guid);
        setIsKeyNoteDrawerOpen(true);
      }
    },
    [],
  );

  const handleNextAppointmentChangeDueDateClick = useCallback(
    (details: INextAppointmentDetails) => {
      if (details?.id && details?.patient?.guid) {
        const appointmentData: INextAppointment = {
          guid: details.id,
          patientGuid: details.patient.guid,
          agencyDoctorGuid: details.agencyDoctorGuid,
          doctorGuid: details.doctorGuid,
          appointmentMethod:
            (details.appointmentMethod as Nullable<AppointmentMethod>) || null,
          scheduledDate: details.appointmentDateTime || '',
          updatedAt: '',
        };
        setSelectedAppointmentToChangeDueDate(appointmentData);
        setIsUpdateDueDateModalOpen(true);
      }
    },
    [],
  );

  const nextAppointmentActions = useMemo<INextAppointmentAction[]>(
    () => [
      {
        id: 'calendar',
        ariaLabel: t('homeDashboard.appointments.actions.openCalendar'),
        tooltip: 'Appointment Due Date',
        icon: <CalendarOutlined />,
        onClick: handleNextAppointmentChangeDueDateClick,
      },
      {
        id: 'notes',
        ariaLabel: t('homeDashboard.appointments.actions.openNotes'),
        tooltip: 'Key Note',
        icon: <SnippetsOutlined />,
        onClick: handleNextAppointmentNotesClick,
      },
      {
        id: 'view',
        ariaLabel: t('homeDashboard.nextAppointment.actions.viewDetails'),
        label: t('homeDashboard.nextAppointment.actions.view'),
        icon: <EyeOutlined />,
      },
    ],
    [
      t,
      handleNextAppointmentNotesClick,
      handleNextAppointmentChangeDueDateClick,
    ],
  );

  const buildCountdownAriaLabel = useCallback(
    (timeLabel?: string) =>
      t('homeDashboard.nextAppointment.countdownAria', {
        time: timeLabel ?? '',
      }),
    [t],
  );
  const handlePatientReadingsPageChange = useCallback((page: number) => {
    setPatientReadingsPage(page);
  }, []);

  const handleTodayAppointmentsPageChange = useCallback((page: number) => {
    setTodayAppointmentsPage(page);
  }, []);

  const handleTodayHistoryPageChange = useCallback((page: number) => {
    setTodayHistoryPage(page);
  }, []);

  const handlePatientReadingsSearchChange = useCallback(
    (searchTerm: string) => {
      setPatientReadingsSearchTerm(searchTerm || undefined);
      setPatientReadingsPage(1);
    },
    [],
  );

  const handleSnippetClick = useCallback(
    (patientGuid: string) => {
      const patientData = patientReadings.find(
        (reading) => reading.patient.guid === patientGuid,
      );

      if (patientData && patientData.readings) {
        const firstReading = patientData.readings;
        const doctorName = `${firstReading.doctor.title || ''} ${
          firstReading.doctor.firstName || ''
        } ${firstReading.doctor.lastName || ''}`.trim();

        setSelectedPatientData({
          guid: patientData.patient.guid,
          firstName: patientData.patient.firstName,
          lastName: patientData.patient.lastName,
          birthDate: patientData.patient.birthDate,
          language: patientData.patient.language,
          doctorName,
          status: patientData.patient.status,
          ccmStatus: patientData.patient.ccmStatus,
        });
        setSelectedPatientGuid(patientGuid);
        setIsKeyNoteDrawerOpen(true);
      }
    },
    [patientReadings],
  );

  const handleCloseKeyNoteDrawer = useCallback(() => {
    setIsKeyNoteDrawerOpen(false);
  }, []);

  const handleUpdateDueDateSuccess = useCallback(() => {
    scheduleNextAppointmentRefresh();
    refetchTodayAppointments();
  }, [scheduleNextAppointmentRefresh, refetchTodayAppointments]);

  return (
    <DashboardLayout>
      <div className={styles.columns}>
        <DashboardSidebar timeframeFilterProps={timeframeFilterProps}>
          <ProductivityGoals
            title={t('homeDashboard.productivity.title')}
            metrics={productivityMetrics}
          />
          <TrendingChart
            title={t('homeDashboard.trending.title')}
            categories={trendingCategories ?? []}
            series={trendingSeries ?? []}
            isLoading={trendingLoading}
            isFetching={trendingFetching}
          />
        </DashboardSidebar>

        <DashboardContent>
          <Row gutter={24}>
            <Col span={11}>
              <Flex vertical gap={16}>
                <NextAppointment
                  title={t('homeDashboard.nextAppointment.title')}
                  details={nextAppointmentDetails}
                  actionButtons={nextAppointmentActions}
                  dateTimeHeading={t(
                    'homeDashboard.nextAppointment.dateTimeLabel',
                  )}
                  startsInHeading={t(
                    'homeDashboard.nextAppointment.startsInLabel',
                  )}
                  emptyMessage={
                    nextAppointmentLoading || nextAppointmentFetching
                      ? t('homeDashboard.nextAppointment.loading')
                      : t('homeDashboard.nextAppointment.empty')
                  }
                  countdownAriaLabelBuilder={buildCountdownAriaLabel}
                  onCountdownComplete={scheduleNextAppointmentRefresh}
                  isLoading={nextAppointmentLoading}
                  isFetching={nextAppointmentFetching}
                />
                <TodayAppointments
                  title={t('homeDashboard.appointments.title')}
                  count={todayAppointmentsTotal}
                  appointments={todayAppointments}
                  emptyMessage={t('homeDashboard.appointments.empty')}
                  actionButtons={todayAppointmentActions}
                  page={todayAppointmentsPage}
                  pageSize={TODAY_APPOINTMENTS_PAGE_SIZE}
                  total={todayAppointmentsTotal}
                  onPageChange={handleTodayAppointmentsPageChange}
                  isLoading={
                    todayAppointmentsLoading || todayAppointmentsFetching
                  }
                />
                <TodayHistoryActivities
                  title={t('homeDashboard.history.title')}
                  entries={todayHistoryEntries}
                  emptyMessage={t('homeDashboard.history.empty')}
                  page={todayHistoryPage}
                  pageSize={TODAY_HISTORY_ACTIVITIES_PAGE_SIZE}
                  total={todayHistoryTotal}
                  isLoading={todayHistoryLoading}
                  isFetching={todayHistoryFetching}
                  onPageChange={handleTodayHistoryPageChange}
                />
              </Flex>
            </Col>
            <Col span={13}>
              <Flex vertical gap={16}>
                <OverviewPatients
                  {...overviewPatientsProps}
                  onStatusCardSelect={handleStatusCardSelect}
                  selectedStatusId={selectedStatusId}
                />
                <PatientReadingsSection
                  patientReadings={patientReadings}
                  isLoading={patientReadingsLoading}
                  isFetching={patientReadingsFetching}
                  page={patientReadingsPage}
                  total={count}
                  searchKeyword={patientReadingsSearchTerm}
                  onPageChange={handlePatientReadingsPageChange}
                  onSearchChange={handlePatientReadingsSearchChange}
                  onSnippetClick={handleSnippetClick}
                />
              </Flex>
            </Col>
          </Row>
        </DashboardContent>
      </div>

      <KeyNotesDrawer
        open={isKeyNoteDrawerOpen}
        onClose={handleCloseKeyNoteDrawer}
        patientGuid={selectedPatientGuid}
        patientInfo={selectedPatientData}
      />

      {isUpdateDueDateModalOpen && selectedAppointmentToChangeDueDate && (
        <UpdateDueDateModal
          title={t('homeDashboard.updateDueDateModal.title')}
          nextAppointment={selectedAppointmentToChangeDueDate}
          open={isUpdateDueDateModalOpen}
          onClose={() => {
            setIsUpdateDueDateModalOpen(false);
            setSelectedAppointmentToChangeDueDate(null);
          }}
          onSuccess={handleUpdateDueDateSuccess}
        />
      )}
    </DashboardLayout>
  );
};

export default HomeDashboardPage;
